/**
 * @file InventoryItemFields.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-30
 * @description The Add / Edit Item form's fields, and the rules that go with
 *              them, lifted out of InventoryItemFormModal so a second screen can
 *              collect an item exactly as Add Item does — the one-step "New
 *              formulation for a customer" (NewCustomerFormulationModal) creates
 *              the item, its formulation and its customer placement in one save.
 *
 *              One copy of: the blank form, the field layout (code, name, units,
 *              Purchased / Sold / Tracked rows with their accounts, tax types and
 *              descriptions), the account and tax-type lists and their Xero
 *              defaults, the required-field validation, and the "this looks like
 *              a customer's or a contract's" text check and its dialog.
 *
 *              The fields are styled by SearchInventory.css and
 *              InventoryItemFormModal.css under `.item-modal`; a host that is not
 *              the item modal wraps them in an element carrying that class.
 *
 *              SOLD, PURCHASED, TRACKED INVENTORY
 *
 *              Each is one row: a tick, and — only once ticked — the fields that
 *              flag brings with it. A new item is NEITHER sold nor purchased
 *              until someone ticks it (Cam, 2026-10-16). Ticking a side copies
 *              the item name into that side's description when it is empty —
 *              never over words someone typed. An unticked side's description
 *              stays on screen (greyed) but is not saved: Xero clears a side's
 *              description and details once its flag is off, and the server
 *              clears them here too (utils/itemSides).
 *              Accounts and tax types are searchable, and the tax types come
 *              from Xero (public.xero_tax_rates, synced nightly) rather than a
 *              list typed into the client that had drifted from the ledger.
 *
 *              1.1.0 (2026-10-14): Edit Item's save — the Xero conflict check and
 *              the PUT / POST with its error wording — moved here
 *              (checkItemConflict, saveItemRequest) so the mineral window
 *              (MineralPricingModal), which now shows these fields for its item,
 *              saves the item through exactly the same request. The fields take
 *              `disabled` (a window in view mode) and `hideCosts` (no
 *              `costs.view`: the purchase price and cost pool, which the server
 *              redacts, read "Hidden" instead of an empty box).
 *
 *              1.2.0 (2026-10-16): Sold / Purchased start unticked; ticking
 *              copies the name into an empty description (itemFormChange).
 *
 *              1.3.0 (2026-10-30, Cam): a changed item code is said before it
 *              is saved — ItemCodeChangeDialog lists the lines here that take
 *              the new code with it; saveItemRequest answers `codeChange` for
 *              the server's 409 and sends `confirm_code_change` once seen.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import Input from './Input';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { getTaxTypeLabel } from '../utils/taxTypes';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
// The unit list, and how a stored weight maps onto it, are shared with the
// read-only item modals — one list, so the form and the view cannot drift.
import { unitValueFor, unitOptionsFor, OTHER_UNIT, unitWeightFromPallet } from '../utils/units';
import './SearchInventory.css';
import './InventoryItemFormModal.css';

/*
 * The account codes that mean this item is milled to a recipe, and therefore
 * wants a formulation. Sales side and purchase side both count — an item can be
 * set up from either, and a blend is a blend whichever way round it was entered.
 *
 * Compared case-insensitively: the codes are citext in the database and so
 * round-trip in whatever case somebody typed, but they are plain strings by the
 * time they reach here.
 */
export const FORMULATION_ACCOUNT_CODES = [
  '200M', '201', '202', '207', '207A',   // sales
  '310M', '311', '312', '317', '317A'    // purchase
];

export const wantsFormulation = (form) => {
  const codes = [form.sales_account_code, form.purchase_account_code]
    .map(c => String(c || '').trim().toUpperCase())
    .filter(Boolean);
  return codes.some(c => FORMULATION_ACCOUNT_CODES.includes(c));
};

export const BLANK_ITEM_FORM = {
  item_code: '',
  item_name: '',
  description: '',
  purchase_description: '',
  // Neither until someone ticks it (Cam, 2026-10-16: "should assume neither").
  is_sold: false,
  is_purchased: false,
  is_tracked_as_inventory: false,
  sales_account_code: '',
  sales_tax_type: '',
  sales_unit_price: '',
  purchase_account_code: '',
  purchase_tax_type: '',
  purchase_unit_price: '',
  cogs_account_code: '',
  quantity_on_hand: 0,
  total_cost_pool: 0,
  unit_weight_kg: '',
  qty_per_pallet: '',
  // Set only on the "Other" unit — see utils/units OTHER_UNIT.
  pallet_weight_kg: '',
  unit_is_other: false
};

export const formFromItem = (item) => ({
  item_code: item.item_code || '',
  item_name: item.item_name || '',
  description: item.description || '',
  purchase_description: item.purchase_description || '',
  is_sold: item.is_sold || false,
  is_purchased: item.is_purchased || false,
  is_tracked_as_inventory: item.is_tracked_as_inventory || false,
  sales_account_code: item.sales_account_code || '',
  sales_tax_type: item.sales_tax_type || '',
  sales_unit_price: item.sales_unit_price ?? '',
  purchase_account_code: item.purchase_account_code || '',
  purchase_tax_type: item.purchase_tax_type || '',
  purchase_unit_price: item.purchase_unit_price ?? '',
  cogs_account_code: item.cogs_account_code || '',
  quantity_on_hand: item.quantity_on_hand || 0,
  total_cost_pool: item.total_cost_pool || 0,
  unit_weight_kg: item.unit_weight_kg ?? '',
  qty_per_pallet: item.qty_per_pallet ?? '',
  pallet_weight_kg: item.pallet_weight_kg ?? '',
  // A pallet weight on file is what makes an item "Other".
  unit_is_other: parseFloat(item.pallet_weight_kg) > 0
});

/**
 * The form after one field changes — the Sold / Purchased and name rules, pure
 * so they can be tested on their own.
 *
 *  - Ticking Sold (Purchased) copies the item name into the sales (purchase)
 *    description when that is empty. Words someone typed are never replaced.
 *  - Unticking leaves the words on screen; they are not saved (the server
 *    clears an unticked side, as Xero does).
 *  - Typing the name keeps a description that still matches the name in step
 *    with it, and fills an empty one only on a side that is ticked.
 */
export const itemFormChange = (prev, field, value) => {
  const updated = { ...prev, [field]: value };
  const blank = (v) => !String(v ?? '').trim();
  if (field === 'is_sold' && value && blank(prev.description)) updated.description = prev.item_name || '';
  if (field === 'is_purchased' && value && blank(prev.purchase_description)) updated.purchase_description = prev.item_name || '';
  if (field === 'item_name') {
    const follows = (desc, ticked) => (desc && desc === prev.item_name) || (blank(desc) && ticked);
    if (follows(prev.description, prev.is_sold)) updated.description = value;
    if (follows(prev.purchase_description, prev.is_purchased)) updated.purchase_description = value;
  }
  return updated;
};

/**
 * The required-field rules. Returns { field: message } — empty when the form
 * can be saved.
 */
export const validateItemForm = (formData) => {
  const errors = {};
  if (!formData.item_code?.trim()) errors.item_code = 'Item code is required';
  if (!formData.item_name?.trim()) errors.item_name = 'Item name is required';
  // "Other" is worked out from the pallet, so it needs both halves.
  if (formData.unit_is_other) {
    if (!(parseFloat(formData.pallet_weight_kg) > 0)) errors.pallet_weight_kg = 'Pallet weight is required for Other';
    if (!(parseFloat(formData.qty_per_pallet) > 0)) errors.qty_per_pallet = 'Qty / pallet is required for Other';
  }
  return errors;
};

/** The form as the server takes it: only "Other" carries a pallet weight. */
export const itemSaveBody = (formData) => ({
  ...formData,
  pallet_weight_kg: formData.unit_is_other ? formData.pallet_weight_kg : ''
});

/**
 * "This looks like a customer's or a contract's" — asked of the text before
 * saving (POST /api/inventory/text-check). An item is the product: a contract
 * number goes stale and shows on every document, and a customer's name or farm
 * in the name or PURCHASE description goes to every supplier. The sales
 * description may name the customer.
 *
 * Resolves to the findings ([] when none, or when the check could not run — a
 * check that cannot run is no reason to stop a save).
 *
 * @param {object} fields  { item_name?, description?, purchase_description? }
 */
export async function checkItemText(fields) {
  if (!Object.keys(fields || {}).length) return [];
  try {
    const r = await fetch(`${API_URL}/api/inventory/text-check`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields)
    });
    const d = await r.json();
    return d.findings || [];
  } catch (err) {
    console.warn('Item text check failed:', err.message);
    return [];
  }
}

/**
 * Does saving this item send the change to Xero? An item Xero has never seen
 * (LOCAL-) has nothing to update there.
 */
export const pushesItemToXero = (item) => !!item?.xero_item_id && !String(item.xero_item_id).startsWith('LOCAL-');

/** The form fields the user has changed since it was seeded (a Set of keys). */
export const changedItemFields = (formData, initialFormData) => {
  const changed = new Set();
  if (!initialFormData) return changed;
  const normalize = (v) => (v === null || v === undefined || v === '') ? '' : String(v);
  for (const key of Object.keys(formData)) {
    if (normalize(formData[key]) !== normalize(initialFormData[key])) changed.add(key);
  }
  return changed;
};

/**
 * Editing a Xero-backed item: has Xero moved underneath us since it was loaded?
 * Resolves to the conflict (for ConflictResolutionModal, with the fields the
 * user changed) or null — including when the check itself cannot run, which is
 * no reason to stop a save.
 */
export async function checkItemConflict(item, formData, initialFormData) {
  try {
    const checkRes = await fetch(
      `${API_URL}/api/inventory/${item.id}/conflict-check?since=${encodeURIComponent(item.xero_updated_date || '')}`,
      { credentials: 'include' }
    );
    const checkData = await checkRes.json();
    if (checkData.hasConflict && checkData.conflicts?.length > 0) {
      return { ...checkData, userChangedFields: changedItemFields(formData, initialFormData) };
    }
  } catch (err) {
    console.warn('Conflict check failed, proceeding with save:', err.message);
  }
  return null;
}

/**
 * Save the item: PUT for an existing item (pushed to Xero first when it is in
 * Xero — the server is sync-or-fail, so a refusal means nothing was written on
 * either side), POST for a new one.
 *
 * @returns {Promise<{ ok: true, item, updatedInXero, data } | { ok: false, message }>}
 */
export async function saveItemRequest({ item = null, formData, resolvedConflicts = null, xeroValues = null, confirmCodeChange = false }) {
  const isEditing = !!item;
  const hasXeroId = isEditing && pushesItemToXero(item);
  try {
    const response = await fetch(
      isEditing ? `${API_URL}/api/inventory/${item.id}` : `${API_URL}/api/inventory`,
      {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          // Only "Other" carries a pallet weight; any other unit clears it.
          ...itemSaveBody(formData),
          ...(hasXeroId && { updateInXero: true }),
          ...(resolvedConflicts && { resolvedConflicts, xeroValues }),
          // The person has seen what a new code reaches (ItemCodeChangeDialog).
          ...(confirmCodeChange && { confirm_code_change: true })
        })
      }
    );
    const data = await response.json();
    if (response.ok && data.success) {
      return { ok: true, item: data.item, updatedInXero: !!data.updatedInXero, codeChange: data.codeChange || null, data };
    }
    // A new code: what it reaches here, to be confirmed first (nothing written yet).
    if (response.status === 409 && data.code === 'ITEM_CODE_CHANGE_CONFIRM') {
      return { ok: false, codeChange: data.code_change || null, message: data.error };
    }
    // Nothing was written on either side.
    return { ok: false, message: data.error || 'Failed to save item.' };
  } catch (error) {
    console.error('Save error:', error);
    return {
      ok: false,
      message: `Couldn't reach the server: ${error.message}. Nothing has been saved — your edits are still here, please try again.`
    };
  }
}

/** The three text fields worth checking: every non-blank one on a create. */
export const textFieldsToCheck = (formData, initialFormData = null, isEditing = false) => {
  const fields = {};
  for (const f of ['item_name', 'description', 'purchase_description']) {
    const now = String(formData[f] || '');
    const was = String(initialFormData?.[f] || '');
    if (now.trim() && (!isEditing || now !== was)) fields[f] = now;
  }
  return fields;
};

/** The warning for checkItemText's findings. The user can save anyway. */
export function ItemTextWarningDialog({ findings, onConfirm, onCancel }) {
  return (
    <ConfirmDialog
      isOpen
      type="warning"
      title="Check the item's wording"
      message={(
        <div className="item-text-warning">
          <p>An item is the product, used for every customer and sent to every supplier. This looks like it names:</p>
          <ul>
            {/* One line per thing found, naming every field it is in — the
                descriptions start as copies of the name, so the same word is
                usually in all three. */}
            {Object.values(findings.reduce((acc, f) => {
              const key = `${f.kind}|${f.text.toLowerCase()}`;
              acc[key] = acc[key] ? { ...acc[key], labels: [...acc[key].labels, f.label] } : { ...f, labels: [f.label] };
              return acc;
            }, {})).map((f, i) => (
              <li key={i}>
                “{f.text}” {f.detail} <em>({f.labels.join(', ').toLowerCase()})</em>
                {f.kind === 'contract'
                  ? ' — contract numbers belong on the contract or order line, not the item.'
                  : ' — keep customer names and addresses out of the item name and purchase description (the sales description may name them).'}
              </li>
            ))}
          </ul>
        </div>
      )}
      confirmText="Save anyway"
      cancelText="Go back and edit"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

/**
 * A new item code reaches every line here that names the old one (the server's
 * services/itemCodeRenameService — Xero shows the new code on its documents
 * already). Said before anything is saved: the sales orders, POs, bills and
 * invoices that change, the still-open ones named.
 *
 * @param {{ plan: object, onConfirm: Function, onCancel: Function }} props —
 *   plan is the 409's code_change ({ old_code, new_code, words: { title,
 *   message, lines, note } })
 */
export function ItemCodeChangeDialog({ plan, onConfirm, onCancel }) {
  const w = plan?.words || {};
  return (
    <ConfirmDialog
      isOpen
      type="warning"
      title={w.title || 'Change the item code?'}
      message={(
        <div className="item-text-warning">
          <p>{w.message}</p>
          {Array.isArray(w.lines) && w.lines.length > 0 && (
            <ul>{w.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
          )}
          {w.note && <p>{w.note}</p>}
          <p>Nothing changes until you save. Amounts are not touched, and nothing more is sent to Xero.</p>
        </div>
      )}
      confirmText={`Change to ${plan?.new_code || 'the new code'}`}
      cancelText="Go back"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

// Code badged, name as the label — the same shape the lines grid uses. Both
// searchable: nobody remembers that fuel is 449. A saved code the list lacks
// (an archived account) is kept selectable rather than silently blanked.
const accountOptions = (accounts, current) => {
  const options = [
    { value: '', label: 'None' },
    ...accounts.map(a => ({
      value: a.code,
      badge: a.code,
      label: a.name || ACCOUNT_NAMES[a.code] || '',
      searchText: `${a.code} ${a.name || ACCOUNT_NAMES[a.code] || ''}`
    }))
  ];
  if (current && !accounts.some(a => String(a.code).toUpperCase() === String(current).toUpperCase())) {
    options.push({ value: current, badge: current, label: ACCOUNT_NAMES[current] || '', searchText: String(current) });
  }
  return options;
};

const taxTypeOptions = (taxTypes, current) => {
  const options = [
    { value: '', label: 'None' },
    ...taxTypes.map(t => ({
      value: t.tax_type,
      label: t.name || getTaxTypeLabel(t.tax_type),
      searchText: `${t.name || ''} ${t.tax_type}`
    }))
  ];
  if (current && !taxTypes.some(t => t.tax_type === current)) {
    options.push({ value: current, label: getTaxTypeLabel(current), searchText: current });
  }
  return options;
};

// The tax type Xero defaults for an account, if the account list knows it.
const accountTaxType = (accounts, code) => {
  if (!code) return null;
  const match = accounts.find(a => String(a.code).toUpperCase() === String(code).toUpperCase());
  return match?.tax_type || null;
};

// Inside a modal body that scrolls: the menus draw fixed, so they are never
// clipped by it.
const MENU_ESCAPE = { escapeOverflow: true };

/**
 * The account and tax-type lists the fields offer, fetched while `isOpen`.
 *
 * Also gives an account code sitting there with no tax type that account's
 * Xero default, once the lists have arrived. Picking an account already does
 * this, but a form seeded from the database (or pre-filled by a caller) can
 * open with an account code and a blank tax type — and a blank is then what
 * gets pushed to Xero. Keyed on the lists, NOT on the form: it fires once when
 * they load and never again, so a tax type the user goes on to clear
 * deliberately is not quietly refilled underneath them. It only fills a blank.
 *
 * @returns {{ salesAccounts, purchaseAccounts, salesTaxTypes, purchaseTaxTypes }}
 */
export function useItemFormLists(isOpen, setFormData) {
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [salesTaxTypes, setSalesTaxTypes] = useState([]);
  const [purchaseTaxTypes, setPurchaseTaxTypes] = useState([]);

  // Fetched here rather than passed in, so a caller that has no use for them
  // otherwise (the order screen) doesn't have to carry them.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const getJson = (path) => fetch(`${API_URL}${path}`, { credentials: 'include' }).then(r => r.json());
        const [salesData, purchaseData, salesTax, purchaseTax] = await Promise.all([
          getJson('/api/inventory/sales-accounts'),
          getJson('/api/inventory/purchase-accounts'),
          getJson('/api/inventory/tax-types?side=sales'),
          getJson('/api/inventory/tax-types?side=purchase')
        ]);
        // Tolerate the old bare-code shape so a stale server mid-deploy doesn't
        // blank these.
        const normalise = (list) => (list || []).map(a => (typeof a === 'string' ? { code: a, name: null } : a));
        if (cancelled) return;
        setSalesAccounts(normalise(salesData.accounts));
        setPurchaseAccounts(normalise(purchaseData.accounts));
        setSalesTaxTypes(salesTax.taxTypes || []);
        setPurchaseTaxTypes(purchaseTax.taxTypes || []);
      } catch (error) {
        console.error('Failed to fetch account codes:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (salesAccounts.length === 0 && purchaseAccounts.length === 0) return;
    setFormData(prev => {
      const salesDefault = !prev.sales_tax_type && prev.sales_account_code
        ? accountTaxType(salesAccounts, prev.sales_account_code) : null;
      const purchaseDefault = !prev.purchase_tax_type && prev.purchase_account_code
        ? accountTaxType(purchaseAccounts, prev.purchase_account_code) : null;
      if (!salesDefault && !purchaseDefault) return prev;
      return {
        ...prev,
        ...(salesDefault && { sales_tax_type: salesDefault }),
        ...(purchaseDefault && { purchase_tax_type: purchaseDefault })
      };
    });
    // setFormData is a state setter: stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesAccounts, purchaseAccounts]);

  return { salesAccounts, purchaseAccounts, salesTaxTypes, purchaseTaxTypes };
}

/**
 * The fields themselves.
 *
 * @param {object}   formData       the form (BLANK_ITEM_FORM / formFromItem shape)
 * @param {function} setFormData    its setter
 * @param {object}   formErrors     { field: message }, from validateItemForm
 * @param {function} setFormErrors  its setter — a field's error clears as it is edited
 * @param {object}   lists          useItemFormLists()
 * @param {object?}  item           the saved item when editing (inventory account, Xero link)
 * @param {function?} onTextEdited  called when a name or description changes, so
 *                                  the host can ask the text check again
 * @param {boolean?} disabled       every field read-only (a window in view mode)
 * @param {boolean?} hideCosts      the caller has no `costs.view`: the purchase
 *                                  price and cost pool read "Hidden"
 */
function InventoryItemFields({
  formData, setFormData, formErrors, setFormErrors, lists, item = null, onTextEdited,
  disabled = false, hideCosts = false
}) {
  const { salesAccounts, purchaseAccounts, salesTaxTypes, purchaseTaxTypes } = lists;
  const isEditing = !!item;
  // The Xero stock-take button appears only once Tracked is ticked: measured
  // here, where it mounts, in whichever window hosts these fields.
  useSlideOutButton([formData.is_tracked_as_inventory, isEditing]);
  const xid = item?.xero_item_id;
  const hasXeroLink = xid && !String(xid).startsWith('LOCAL-') && !String(xid).startsWith('TEMP-');

  const handleFormChange = (field, value) => {
    // The Sold / Purchased copy-on-tick and the name-following rule
    // (itemFormChange): the common case needs no typing, and a deliberate edit
    // is never clobbered.
    setFormData(prev => itemFormChange(prev, field, value));
    if (['item_name', 'description', 'purchase_description', 'is_sold', 'is_purchased'].includes(field)) onTextEdited?.();
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  // Choosing an account brings its default tax type with it — the one Xero
  // would pick for that account — and the tax type can still be changed after.
  const handleAccountChange = (accountField, taxField, accounts, code) => {
    handleFormChange(accountField, code);
    const taxType = accountTaxType(accounts, code);
    if (taxType) handleFormChange(taxField, taxType);
  };

  /*
   * Choosing a unit sets the weight it implies. "Other" takes its weight from
   * the pallet instead, so it clears the unit weight (the server works it out
   * from pallet weight / qty per pallet) and asks for the pallet weight;
   * leaving "Other" drops the pallet weight, which is what marked it.
   */
  const handleUnitChange = (value) => {
    if (value === OTHER_UNIT) {
      setFormData(prev => ({ ...prev, unit_is_other: true, unit_weight_kg: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, unit_is_other: false, pallet_weight_kg: '', unit_weight_kg: value }));
  };

  return (
  <div className="item-details edit-form">
    <div className="detail-row code-name">
      <div className="detail-group">
        <label>Item Code <span className="required">*</span></label>
        <Input
          type="text"
          value={formData.item_code || ''}
          onChange={(e) => handleFormChange('item_code', e.target.value)}
          error={formErrors.item_code}
          placeholder="e.g. PROD001"
          disabled={disabled}
        />
      </div>
      <div className="detail-group">
        <label>Item Name <span className="required">*</span></label>
        <Input
          type="text"
          value={formData.item_name || ''}
          onChange={(e) => handleFormChange('item_name', e.target.value)}
          error={formErrors.item_name}
          placeholder="e.g. Fertilizer 25kg"
          disabled={disabled}
        />
      </div>
    </div>

    {/* Units — derives delivery tonnage in transport planning. The
        weight is filled from the choice and not typed. Blank = bulk
        (1 unit = 1 tonne). */}
    <div className="detail-row units">
      <div className="detail-group">
        <label>Units</label>
        <Dropdown
          value={formData.unit_is_other ? OTHER_UNIT : unitValueFor(formData.unit_weight_kg)}
          onChange={handleUnitChange}
          placeholder="Select unit…"
          disabled={disabled}
          options={unitOptionsFor(formData.unit_is_other ? '' : formData.unit_weight_kg)}
        />
      </div>
      {/* "Other" is described by its pallet: the pallet's weight here,
          its quantity in Qty / Pallet, and the unit weight follows. */}
      {formData.unit_is_other && (
        <div className="detail-group">
          <label>Pallet weight (kg) <span className="required">*</span></label>
          <Input
            type="number" step="0.01" min="0"
            value={formData.pallet_weight_kg ?? ''}
            onChange={(e) => handleFormChange('pallet_weight_kg', e.target.value)}
            error={formErrors.pallet_weight_kg}
            placeholder="e.g. 800"
            disabled={disabled}
          />
        </div>
      )}
      <div className="detail-group">
        <label>Weight per unit</label>
        <div
          className="detail-value unit-weight"
          title={formData.unit_is_other ? 'Pallet weight ÷ qty per pallet' : 'Set by the unit chosen'}
        >
          {(() => {
            const kg = formData.unit_is_other
              ? unitWeightFromPallet(formData.pallet_weight_kg, formData.qty_per_pallet)
              : Number(unitValueFor(formData.unit_weight_kg)) || null;
            if (kg) return `${Number(kg.toFixed(2)).toLocaleString('en-GB')}kg`;
            return formData.unit_is_other ? 'Enter pallet weight and qty' : 'Select Unit...';
          })()}
        </div>
      </div>
      {/* Typed, not derived — how the product happens to be stacked
          is not implied by the unit. Blank is fine and common: it
          then shows nowhere, including under the order grid's Qty
          box. */}
      <div className="detail-group">
        <label>Qty / Pallet{formData.unit_is_other && <> <span className="required">*</span></>}</label>
        <Input
          type="number" step="0.01" min="0"
          value={formData.qty_per_pallet ?? ''}
          onChange={(e) => handleFormChange('qty_per_pallet', e.target.value)}
          error={formErrors.qty_per_pallet}
          placeholder="e.g. 48"
          disabled={disabled}
        />
      </div>
    </div>

    {/* Purchased / Sold / Tracked Inventory: a tick each, and the
        fields that come with it only once it is ticked. That side's
        description is the exception — it stays on screen, greyed
        while the side is off: its words are not saved (Xero clears
        a side's description once the flag is off). */}
    <div className="item-flags">
      <div className={`item-flag-row ${formData.is_purchased ? 'is-on' : ''}`}>
        <TickCheckbox
          id="inv-form-is-purchased"
          checked={!!formData.is_purchased}
          onChange={(e) => handleFormChange('is_purchased', e.target.checked)}
          label="Purchased"
          size={18}
          disabled={disabled}
        />
        {formData.is_purchased && (
          <div className="item-flag-fields">
            <div className="detail-group">
              <label>Unit Price</label>
              {hideCosts ? (
                <div className="detail-value" title="Needs the See cost & margin permission">Hidden</div>
              ) : (
                <Input
                  type="number" step="0.01"
                  value={formData.purchase_unit_price ?? ''}
                  onChange={(e) => handleFormChange('purchase_unit_price', e.target.value)}
                  placeholder="0.00"
                  disabled={disabled}
                />
              )}
            </div>
            {/* A tracked item has no purchase account in Xero: its
                purchases post to its inventory account, which Xero
                sets — shown read-only, so nobody types a purchase
                account the nightly sync would then wipe
                (utils/itemAccounts). */}
            {formData.is_tracked_as_inventory ? (
              <div className="detail-group">
                <label>Inventory Account</label>
                <div className="detail-value readonly-account"
                     title="Set in Xero for tracked items; mirrored here by the item sync">
                  {item?.inventory_asset_account_code || <span className="muted">Set in Xero — appears after the next full sync</span>}
                </div>
              </div>
            ) : (
              <div className="detail-group">
                <label>Account Code</label>
                <SearchableDropdown
                  value={formData.purchase_account_code || ''}
                  onChange={(value) => handleAccountChange('purchase_account_code', 'purchase_tax_type', purchaseAccounts, value)}
                  options={accountOptions(purchaseAccounts, formData.purchase_account_code)}
                  placeholder="Select account"
                  searchPlaceholder="Search by code or name..."
                  disabled={disabled}
                  {...MENU_ESCAPE}
                />
              </div>
            )}
            <div className="detail-group">
              <label>Tax Type</label>
              <SearchableDropdown
                value={formData.purchase_tax_type || ''}
                onChange={(value) => handleFormChange('purchase_tax_type', value)}
                options={taxTypeOptions(purchaseTaxTypes, formData.purchase_tax_type)}
                placeholder="Select tax type"
                searchPlaceholder="Search tax types..."
                disabled={disabled}
                {...MENU_ESCAPE}
              />
            </div>
          </div>
        )}
        {/* Shown whether or not the tick is on, greyed while it is
            off: unticking leaves the words on screen (ticking again
            brings them back) but they are not saved — Xero clears an
            unticked side's description, and so does the server. Named
            for the side it belongs to: "Description" on its own reads
            as a third, general one, and Xero has only these two. */}
        <div className="detail-group item-flag-description">
          <label>Purchase Description</label>
          <textarea
            className="form-textarea"
            value={formData.purchase_description || ''}
            onChange={(e) => handleFormChange('purchase_description', e.target.value)}
            placeholder={formData.is_purchased ? 'Used on purchase orders and bills' : 'Tick Purchased to give it a purchase description'}
            title={formData.is_purchased ? undefined : 'Not saved while Purchased is unticked — Xero holds no purchase description for an item that is not purchased'}
            rows="1"
            disabled={disabled || !formData.is_purchased}
          />
        </div>
      </div>

      <div className={`item-flag-row ${formData.is_sold ? 'is-on' : ''}`}>
        <TickCheckbox
          id="inv-form-is-sold"
          checked={!!formData.is_sold}
          onChange={(e) => handleFormChange('is_sold', e.target.checked)}
          label="Sold"
          size={18}
          disabled={disabled}
        />
        {formData.is_sold && (
          <div className="item-flag-fields">
            <div className="detail-group">
              <label>Unit Price</label>
              <Input
                type="number" step="0.01"
                value={formData.sales_unit_price ?? ''}
                onChange={(e) => handleFormChange('sales_unit_price', e.target.value)}
                placeholder="0.00"
                disabled={disabled}
              />
            </div>
            <div className="detail-group">
              <label>Account Code</label>
              <SearchableDropdown
                value={formData.sales_account_code || ''}
                onChange={(value) => handleAccountChange('sales_account_code', 'sales_tax_type', salesAccounts, value)}
                options={accountOptions(salesAccounts, formData.sales_account_code)}
                placeholder="Select account"
                searchPlaceholder="Search by code or name..."
                disabled={disabled}
                {...MENU_ESCAPE}
              />
            </div>
            <div className="detail-group">
              <label>Tax Type</label>
              <SearchableDropdown
                value={formData.sales_tax_type || ''}
                onChange={(value) => handleFormChange('sales_tax_type', value)}
                options={taxTypeOptions(salesTaxTypes, formData.sales_tax_type)}
                placeholder="Select tax type"
                searchPlaceholder="Search tax types..."
                disabled={disabled}
                {...MENU_ESCAPE}
              />
            </div>
          </div>
        )}
        {/* The sales description — Xero's `Description`. Shown, and
            greyed while Sold is off, for the same reason as the
            purchase one above. */}
        <div className="detail-group item-flag-description">
          <label>Sales Description</label>
          <textarea
            className="form-textarea"
            value={formData.description || ''}
            onChange={(e) => handleFormChange('description', e.target.value)}
            placeholder={formData.is_sold ? 'Used on quotes and invoices' : 'Tick Sold to give it a sales description'}
            title={formData.is_sold ? undefined : 'Not saved while Sold is unticked — Xero holds no sales description for an item that is not sold'}
            rows="1"
            disabled={disabled || !formData.is_sold}
          />
        </div>
      </div>

      {/* Quantity on hand and the cost pool are read-only: Xero's API
          does not accept stock adjustments on the Item resource, so
          anything typed here would be silently reverted by the next
          sync. Send people to Xero; the nightly sync mirrors it back. */}
      <div className={`item-flag-row ${formData.is_tracked_as_inventory ? 'is-on' : ''}`}>
        <TickCheckbox
          id="inv-form-is-tracked"
          checked={!!formData.is_tracked_as_inventory}
          onChange={(e) => handleFormChange('is_tracked_as_inventory', e.target.checked)}
          label="Tracked Inventory"
          size={18}
          disabled={disabled}
        />
        {formData.is_tracked_as_inventory && (
          <div className="item-flag-fields stock-tracking">
            <div className="detail-group">
              <label>COGS Account</label>
              <SearchableDropdown
                value={formData.cogs_account_code || ''}
                onChange={(value) => handleFormChange('cogs_account_code', value)}
                options={accountOptions(purchaseAccounts, formData.cogs_account_code)}
                placeholder="Select account"
                searchPlaceholder="Search by code or name..."
                disabled={disabled}
                {...MENU_ESCAPE}
              />
            </div>
            <div className="detail-group">
              <label>Quantity on Hand</label>
              <Input type="number" step="0.001" value={formData.quantity_on_hand || 0} disabled readOnly />
            </div>
            <div className="detail-group">
              <label>Total Cost Pool</label>
              {hideCosts
                ? <div className="detail-value" title="Needs the See cost & margin permission">Hidden</div>
                : <Input type="number" step="0.01" value={formData.total_cost_pool || 0} disabled readOnly />}
            </div>
            <div className="detail-group stock-take-cta">
              <p className="stock-take-note">
                Stock-takes must be performed in Xero. Changes will appear here after the next sync.
              </p>
              <button
                type="button"
                className="btn-slideout"
                onClick={() => window.open(
                  hasXeroLink
                    ? `https://go.xero.com/Accounts/Inventory/${encodeURIComponent(xid)}`
                    : 'https://go.xero.com/Accounts/Inventory',
                  '_blank', 'noopener,noreferrer'
                )}
                disabled={!isEditing}
                title={hasXeroLink ? 'Open this item in Xero' : 'Open inventory list in Xero'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">open_in_new</span>
                </div>
                <span className="btn-label">{hasXeroLink ? 'Open in Xero' : 'Open Xero inventory'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
  );
}

export default InventoryItemFields;
